import React, { useState } from 'react';
import {
  Modal,
  Tabs,
  Row,
  Col,
  Button,
  Upload,
  message,
  Typography,
  Spin,
} from 'antd';
import {
  UploadOutlined,
} from '@ant-design/icons';
import { SourceEpisode, SourcePanel } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';

const { Text } = Typography;

interface HookPanelImagePickerModalProps {
  open: boolean;
  episodeId: string;
  hookId: string;
  panelId: string;
  episodePanels: SourcePanel[];
  onSuccess: (episode: SourceEpisode) => void;
  onClose: () => void;
}

const HookPanelImagePickerModal: React.FC<HookPanelImagePickerModalProps> = ({
  open,
  episodeId,
  hookId,
  panelId,
  episodePanels,
  onSuccess,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleSelectFromEpisode = async (imageUrl: string) => {
    setLoading(true);
    try {
      const result = await sourceEpisodesApi.setHookPanelImage(episodeId, hookId, panelId, imageUrl);
      message.success('Image set successfully');
      onSuccess(result.data);
      onClose();
    } catch (error: any) {
      console.error('Failed to set image:', error);
      message.error(error.response?.data?.error || 'Failed to set image');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      message.error('Please select an image first');
      return;
    }

    setLoading(true);
    try {
      const result = await sourceEpisodesApi.uploadHookPanelImage(episodeId, hookId, panelId, uploadFile);
      message.success('Image uploaded and set successfully');
      onSuccess(result.data);
      onClose();
    } catch (error: any) {
      console.error('Failed to upload image:', error);
      message.error(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const onBeforeUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/gif';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG/WEBP/GIF file!');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Image must smaller than 10MB!');
      return Upload.LIST_IGNORE;
    }

    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target?.result as string);
    reader.readAsDataURL(file);
    return false; // Prevent auto upload
  };

  // Filter panels that actually have images or videos with first frames
  const availablePanels = episodePanels.filter(p => p.imagePath || p.firstFrameUrl);

  return (
    <Modal
      title="Select Image for Hook Panel"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      style={{ maxWidth: '95%' }}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Pick from Episode" key="1">
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px' }}>
              <Row gutter={[12, 12]}>
                {availablePanels.length === 0 ? (
                  <Col span={24}>
                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                      <Text type="secondary">No panels with images found in this episode.</Text>
                    </div>
                  </Col>
                ) : (
                  availablePanels.map((panel) => {
                    const thumbUrl = panel.imagePath || panel.firstFrameUrl;
                    return (
                      <Col xs={12} sm={8} key={panel.id}>
                        <div 
                          className="image-picker-item"
                          style={{
                            cursor: 'pointer',
                            border: '1px solid #f0f0f0',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            aspectRatio: '1/1',
                            position: 'relative',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                          onClick={() => thumbUrl && handleSelectFromEpisode(thumbUrl)}
                        >
                          <img 
                            src={thumbUrl} 
                            alt={panel.caption || 'Panel'} 
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            #{panel.sceneOrderIndex} {panel.caption}
                          </div>
                        </div>
                      </Col>
                    );
                  })
                )}
              </Row>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Upload New" key="2">
            <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={onBeforeUpload}
                accept="image/*"
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Click to select</div>
                  </div>
                )}
              </Upload>
              
              <div style={{ marginTop: 24, width: '100%', maxWidth: 300 }}>
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />} 
                  onClick={handleUpload}
                  disabled={!uploadFile}
                  loading={loading}
                  block
                >
                  Upload & Use this image
                </Button>
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Spin>
    </Modal>
  );
};

export default HookPanelImagePickerModal;
